import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, FormAlert, Input, Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { checkProjectDomain } from '../../services/projectDomain';
import { fetchCustomInstructions, refusalMessage, saveCustomInstructions } from '../../services/customInstructions';
import { useProjectStacks } from '../../hooks/useProjectStacks';
import { StackPicks, editShowsInstructions, editedInstructions } from '../../utils/stackPicker';
import { slugify } from '../../utils/slug';
import { sanitizeChange } from '../../utils/sanitizeInput';
import { ProjectLlmSelect } from './ProjectLlmSelect';
import { InstructionsField } from './InstructionsField';
import { GENERATED_APP_BASE_DOMAIN } from '../../config/domains';

const DOMAIN_CHECK_DEBOUNCE_MS = 450;

/** The line under the Special Instructions box while Save Changes can save it. */
const CHANGES_APPLY = 'Changes apply from the next message.';

interface DomainStatus {
  kind: 'idle' | 'checking' | 'available' | 'taken' | 'error';
  msg?: string;
}

/** The project's special instructions, as the drawer read them when it opened. */
type Instructions =
  | { kind: 'idle' | 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; saved: string; maxChars: number };

interface EditProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Saves the project's fields and resolves to whether it did, in which case it
   *  had the project list read again: after saving the special instructions, the
   *  drawer waits for it and has the list read again only when it didn't. */
  onSubmit: (e: React.FormEvent) => Promise<boolean>;
  loading: boolean;
  error: string;
  name: string;
  setName: (v: string) => void;
  clientName: string;
  setClientName: (v: string) => void;
  customDomain?: string;
  setCustomDomain: (v: string) => void;
  projectType?: number;
  promptVersion?: number | null;
  projectId?: string;
  /** The project's tech stack: its label, shown read-only over the Special
   *  Instructions box, and its picks (group -> part id), from which the drawer
   *  knows whether the special instructions are its whole tech stack. */
  stackLabel?: string | null;
  stackPicks?: StackPicks | null;
  /** After Save Changes saved the special instructions but not the project's
   *  other fields: the project's flag may have changed. */
  onCustomInstructionsSaved?: () => void;
  /** Beta-user-only: the LLM control is rendered only when true. */
  canChooseLlm?: boolean;
  /** The project's chosen service, or null for the platform default. */
  llmService?: string | null;
  setLlmService?: (service: string | null) => void;
  /** Set when the project's stored service is no longer offered. */
  llmProblem?: string | null;
}

/**
 * The Edit project drawer. Where the platform can change a Web project's special
 * instructions, while the Custom option is offered (editShowsInstructions), it
 * shows after Client Name the project's tech stack, read-only, and its Special
 * Instructions box, read as the drawer opens (multi-stack step 3.14i, E1 of the
 * owner's pick, 2026-10-20). Save Changes saves the box's text first, once it is
 * read and only when it changed, then the project's fields (editedInstructions);
 * a refused or failed instructions save keeps the drawer open with why, and the
 * fields unsaved. Save Changes stays off while the text is over the cap, or empty
 * on a project made from special instructions alone (T1). Everywhere else the
 * drawer is as it was.
 */
export const EditProjectModal: React.FC<EditProjectModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  error,
  name,
  setName,
  clientName,
  setClientName,
  customDomain,
  setCustomDomain,
  projectType,
  promptVersion,
  projectId,
  stackLabel,
  stackPicks,
  onCustomInstructionsSaved,
  canChooseLlm = false,
  llmService = null,
  setLlmService,
  llmProblem,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [initialDomain, setInitialDomain] = useState<string | undefined>('');
  const alertRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLElement>(null);

  // Special instructions are for Web projects on prompt version 2, as custom domains
  // are, and only while the server offers the Custom option: the stacks list says
  // whether it does, read each time the drawer opens on such a project.
  const newerWebProject = projectType === 1 && (promptVersion || 0) >= 2;
  const { state: stacksState } = useProjectStacks(isOpen && newerWebProject);
  const customPicker = stacksState.kind === 'loaded' ? stacksState.custom : null;
  const showsInstructions = !!projectId && editShowsInstructions({ projectType, promptVersion, stackLabel }, customPicker);

  const [instructions, setInstructions] = useState<Instructions>({ kind: 'idle' });
  const [instructionsText, setInstructionsText] = useState('');
  const [savingInstructions, setSavingInstructions] = useState(false);
  const [instructionsError, setInstructionsError] = useState('');
  // Drops a reply that a later read overtook, or that comes after the drawer closed.
  const readSeq = useRef(0);

  const loadInstructions = useCallback(async () => {
    if (!projectId) return;
    const id = ++readSeq.current;
    setInstructions({ kind: 'loading' });
    try {
      const read = await fetchCustomInstructions(projectId);
      if (id !== readSeq.current) return;
      setInstructionsText(read.text);
      setInstructions({ kind: 'loaded', saved: read.text, maxChars: read.max_chars });
    } catch (err) {
      if (id === readSeq.current) {
        setInstructions({ kind: 'error', message: refusalMessage(err) ?? "Special instructions couldn't be loaded." });
      }
    }
  }, [projectId]);

  // Read once each time the drawer opens where the box shows, and forgotten when it closes.
  useEffect(() => {
    if (isOpen) return;
    readSeq.current++;
    setInstructions({ kind: 'idle' });
    setInstructionsText('');
    setInstructionsError('');
  }, [isOpen]);
  useEffect(() => {
    if (isOpen && showsInstructions && instructions.kind === 'idle') loadInstructions();
  }, [isOpen, showsInstructions, instructions.kind, loadInstructions]);

  // What Save Changes does with the box, once its text is read: nothing before.
  const loadedInstructions = showsInstructions && instructions.kind === 'loaded' ? instructions : null;
  const edited = loadedInstructions
    ? editedInstructions(stackPicks, loadedInstructions.saved, instructionsText, loadedInstructions.maxChars)
    : null;
  const saving = loading || savingInstructions;
  // Where the box shows, Save Changes may save the special instructions before the
  // fields, which it sends as they were when it was pressed: so the fields, like
  // the box, can't change while it saves. Elsewhere they stay as they were.
  const fieldsLocked = saving && showsInstructions;

  useEffect(() => {
    if (isOpen) setInitialDomain(customDomain);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const [domainStatus, setDomainStatus] = useState<DomainStatus>({ kind: 'idle' });
  // Drops responses that no longer describe the current value.
  const checkSeq = useRef(0);

  const normalizedCurrent = customDomain?.trim() || '';
  const normalizedInitial = initialDomain?.trim() || '';
  const isInitialDomain = normalizedCurrent === normalizedInitial;

  // Checked as the user types, debounced. The project's own current domain is
  // not a candidate — no check, no verdict, until the value actually changes.
  const domainCheckEnabled = isOpen && !!normalizedCurrent && !isInitialDomain;

  useEffect(() => {
    checkSeq.current++;
    if (!domainCheckEnabled) {
      setDomainStatus({ kind: 'idle' });
      return;
    }
    setDomainStatus({ kind: 'checking' });
    const id = checkSeq.current;
    const timer = setTimeout(async () => {
      try {
        const result = await checkProjectDomain(normalizedCurrent, projectId);
        if (id !== checkSeq.current) return;
        setDomainStatus(
          result.available
            ? { kind: 'available' }
            : { kind: 'taken', msg: result.reason || 'Domain is unavailable' }
        );
      } catch {
        if (id !== checkSeq.current) return;
        setDomainStatus({ kind: 'error', msg: "Couldn't reach the server. It'll retry as you type." });
      }
    }, DOMAIN_CHECK_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedCurrent, domainCheckEnabled]);

  // Also off while the box's text is one the platform would refuse: over the cap,
  // or empty on a project made from special instructions alone (T1).
  const isSubmitDisabled =
    saving || !name.trim() || (!isInitialDomain && !!normalizedCurrent && domainStatus.kind !== 'available') || !!edited?.refusal;

  // A failed save shows at the end of the form, scrolled into view.
  useEffect(() => {
    if (instructionsError) alertRef.current?.scrollIntoView({ block: 'nearest' });
  }, [instructionsError]);
  useEffect(() => {
    if (isOpen && error) errorRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, error]);

  const domainBad = domainStatus.kind === 'taken' || domainStatus.kind === 'error';
  const domainMessage =
    domainStatus.msg ||
    (domainStatus.kind === 'checking'
      ? 'Checking availability…'
      : domainStatus.kind === 'available'
      ? 'Domain is available.'
      : '');
  const domainIcon =
    domainStatus.kind === 'checking' ? (
      <Icon name={IconName.Loading} size="sm" spin className="text-text-tertiary" inheritColor />
    ) : domainStatus.kind === 'available' ? (
      <Icon name={IconName.Check} size="sm" variant="success" />
    ) : domainBad ? (
      <Icon name={IconName.Close} size="sm" variant="error" />
    ) : undefined;

  // Shared with the environment form's name and domain fields: one rule for
  // what a URL-safe label may contain, and one helper that keeps the caret
  // where the user left it. Sanitizing on every keystroke rewrites the whole
  // value, which otherwise throws the cursor to the end of the field every
  // time someone types a space, an underscore or a capital.
  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomDomain(sanitizeChange(e, slugify));
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  // Save Changes: the special instructions first, where they were read and their
  // text changed, then the project's fields. A refused or failed instructions
  // save keeps the drawer open with why, and the fields unsaved. Once they are
  // saved the project list is read again, once, so the project's flag is current:
  // by the fields' save, or here when the fields then fail to save; a second Save
  // Changes then sends the fields alone.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitDisabled) return;
    setInstructionsError('');
    let instructionsSaved = false;
    if (projectId && loadedInstructions && edited?.changed) {
      setSavingInstructions(true);
      try {
        const kept = await saveCustomInstructions(projectId, instructionsText);
        setInstructions({ kind: 'loaded', saved: kept.text, maxChars: kept.max_chars });
        instructionsSaved = true;
      } catch (err) {
        setInstructionsError(refusalMessage(err) ?? "Special instructions couldn't be saved. Please try again.");
        return;
      } finally {
        setSavingInstructions(false);
      }
    }
    const fieldsSaved = await onSubmit(e);
    if (instructionsSaved && !fieldsSaved) onCustomInstructionsSaved?.();
  };

  if (!isOpen && !isClosing) return null;

  // A click on the backdrop closes the drawer, but not while it saves the special
  // instructions or while its box holds a change: Cancel and the close button
  // discard that, as the Create and Clone drawers keep special instructions.
  const backdropCloses = !savingInstructions && !edited?.changed;

  return (
    <div 
      className={`fixed inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`} 
      onClick={backdropCloses ? handleClose : undefined}
    >
      <div
        className={`absolute right-0 top-0 h-full bg-bg-primary w-[400px] flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)"
        }}
      >
        {/* Top Section */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
          <div>
            <Text variant="text-xl" weight="bold">Edit Project</Text>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
            disabled={saving}
          >
            <Icon name={IconName.Cross} size="lg" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        {/* The form takes the height left and its middle scrolls, so the footer
            never drops below the screen and stays at the drawer's foot. */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Middle Section */}
          <div className="flex flex-col items-center gap-bg-v2-3xl self-stretch px-bg-v2-3xl py-bg-v2-3xl overflow-y-auto flex-1 min-h-0">
            <div className="w-full space-y-6">
              <Input
                id="project-name-edit"
                name="project-name-edit"
                label="Project Name"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                autoFocus
                placeholder="Enter project name"
                className="w-full"
                disabled={fieldsLocked}
              />
              <Input
                id="client-name-edit"
                name="client-name-edit"
                label="Client Name"
                value={clientName}
                onChange={e => setClientName(e.target.value)}
                placeholder="Enter client name"
                className="w-full"
                disabled={fieldsLocked}
              />

              {/* The project's tech stack, read-only, and its Special Instructions
                  box, which waits for the text to be read. */}
              {showsInstructions && (
                <>
                  <div className="h-px bg-border-secondary w-full" />
                  <div className="flex flex-col gap-bg-v2-xs">
                    <Text variant="text-md" weight="semibold" color="primary">Tech Stack</Text>
                    <Text variant="text-sm" weight="semibold" color="tertiary">{stackLabel}</Text>
                  </div>
                  {loadedInstructions && edited ? (
                    <InstructionsField
                      id="project-custom-instructions"
                      value={instructionsText}
                      onChange={(value) => {
                        setInstructionsText(value);
                        setInstructionsError('');
                      }}
                      maxChars={loadedInstructions.maxChars}
                      hint={edited.refusal ?? CHANGES_APPLY}
                      disabled={saving}
                    />
                  ) : instructions.kind === 'error' ? (
                    <div className="flex items-center gap-bg-v2-md">
                      <Text variant="text-sm" color="error" as="span">{instructions.message}</Text>
                      <Button type="button" variant="link" size="small" label="Try again" onClick={loadInstructions} className="shrink-0" />
                    </div>
                  ) : (
                    <Text variant="text-sm" color="tertiary">Loading special instructions…</Text>
                  )}
                </>
              )}
              
              {projectType === 1 && (promptVersion || 0) >= 2 && (
                <div className="flex-1 min-w-[280px]">
                  <Text variant="text-md" weight="semibold" color="primary" className="mb-3">Custom Domain</Text>
                  <Input
                    id="custom-domain-edit"
                    name="custom-domain-edit"
                    value={customDomain || ''}
                    onChange={handleDomainChange}
                    autoComplete="off"
                    spellCheck={false}
                    variant={domainStatus.kind === 'available' ? 'success' : domainBad ? 'error' : 'default'}
                    endIcon={domainIcon}
                    suffix={`.${GENERATED_APP_BASE_DOMAIN}`}
                    disabled={fieldsLocked}
                  />
                  {domainMessage && (
                    <Text
                      variant="text-xs"
                      color={domainBad ? 'error' : domainStatus.kind === 'available' ? 'success' : 'tertiary'}
                      className="mt-bg-v2-xs break-words block"
                    >
                      {domainMessage}
                    </Text>
                  )}
                </div>
              )}

              <ProjectLlmSelect
                id="project-llm-edit"
                visible={canChooseLlm}
                value={llmService}
                onChange={service => setLlmService?.(service)}
                disabled={saving}
                problem={llmProblem}
              />
            </div>

            {/* Why Save Changes failed: the special instructions, whose save comes
                first, or else the project's fields. */}
            {instructionsError ? (
              <div ref={alertRef} className="w-full">
                <FormAlert variant="error" message={instructionsError} />
              </div>
            ) : error && <Text ref={errorRef} variant="text-sm" color="error" className="w-full">{error}</Text>}
          </div>

          {/* Last Section */}
          <div className="flex p-bg-v2-xl px-bg-v2-3xl justify-end items-center gap-bg-v2-xl self-stretch border-t border-border-secondary bg-bg-primary">
            <Button
              variant="outline"
              label="Cancel"
              type="button"
              onClick={handleClose}
              disabled={saving}
            />
            <Button
              variant="primary"
              type="submit"
              label={saving ? 'Saving…' : 'Save Changes'}
              disabled={isSubmitDisabled}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

